import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

interface StockItem {
  productId: string;
  productNo: string;
  productName: string;
  category: string;
  openingStock: number;
  purchase: number;
  sales: number;
  closingStock: number;
  hasInventory: boolean;
}

interface BrandsResponse {
  brands: string[];
}

const StockDisplay: React.FC = () => {
  const { user } = useAuth();
  const [stockData, setStockData] = useState<StockItem[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchMethod, setSearchMethod] = useState<"filter" | "direct">(
    "direct"
  );
  const [selectedBrand, setSelectedBrand] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [date, setDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );

  // Load available brands
  useEffect(() => {
    const loadBrands = async () => {
      try {
        const response = await api.get("/inventory/brands");
        setBrands(response.data.brands);
      } catch (err) {
        console.error("Failed to load brands:", err);
      }
    };
    loadBrands();
  }, []);

  // Load stock data
  const loadStockData = async () => {
    setLoading(true);
    setError(null);

    try {
      let url = `/inventory/search?method=${searchMethod}&date=${date}`;

      if (searchMethod === "filter" && selectedBrand) {
        url += `&category=${selectedBrand}`;
        if (searchQuery) {
          url += `&query=${searchQuery}`;
        }
      } else if (searchMethod === "direct" && searchQuery) {
        url += `&query=${searchQuery}`;
      }

      const response = await api.get(url);
      setStockData(response.data.products || []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load stock data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStockData();
  }, [searchMethod, selectedBrand, searchQuery, date]);

  // Group data by category for display
  const groupedData = stockData.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, StockItem[]>);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Stock Display</h1>
        <p className="text-gray-600">View opening stock for all products</p>
      </div>

      {/* Search Controls */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Date Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Search Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Method
            </label>
            <select
              value={searchMethod}
              onChange={(e) =>
                setSearchMethod(e.target.value as "filter" | "direct")
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="direct">Direct Search</option>
              <option value="filter">Filter by Brand</option>
            </select>
          </div>

          {/* Brand Selector (only for filter method) */}
          {searchMethod === "filter" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand
              </label>
              <select
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Brand</option>
                {brands.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Search Query */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {searchMethod === "filter"
                ? "Product Name/Number"
                : "Product Name/Number"}
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                searchMethod === "filter"
                  ? "Search within brand..."
                  : "Search all products..."
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            
            <button
              onClick={loadStockData}
              disabled={loading}
              className="mt-7 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Loading..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Stock Tables by Category */}
      {Object.keys(groupedData).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(groupedData).map(([category, items]) => (
            <div key={category} className="bg-white rounded-lg shadow-md">
              <div className="bg-gray-50 px-6 py-4 border-b">
                <h2 className="text-xl font-semibold text-gray-800">
                  {category}
                </h2>
                <p className="text-sm text-gray-600">
                  {items.length} products - Opening stock only
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Product Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Opening Stock
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {items.map((item) => (
                      <tr key={item.productId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.productNo}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {item.productName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.openingStock.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <p className="text-gray-500">
            No stock data found for the selected criteria.
          </p>
        </div>
      )}
    </div>
  );
};

export default StockDisplay;
